/*
==========================================================
AVA VISION HUD
American Global
==========================================================

Regular users receive simple vision-health guidance.
Verified Admin accounts retain detailed diagnostics.
*/

import React, {
  useEffect,
  useState,
} from "react";

import {
  getLatestFrame,
  getLatestVision,
} from "../services/avaVisionContext";

import "../styles/AvaVisionHUD.css";

interface AvaVisionHUDProps {
  isAdmin?: boolean;
}

interface SafeVisionStatus {
  label: string;
  message: string;
  tone: "good" | "warning" | "neutral";
}

function readSceneSummary(vision: any): string {
  const scene = vision?.scene;

  if (typeof scene === "string") {
    return scene || "Waiting for visual information.";
  }

  return (
    scene?.summary ||
    scene?.description ||
    "Waiting for visual information."
  );
}

function buildSafeStatus(
  vision: any
): SafeVisionStatus {
  const suppliedStatus =
    vision?.vision_status;

  if (
    suppliedStatus &&
    typeof suppliedStatus === "object"
  ) {
    return {
      label:
        suppliedStatus.label ||
        "Vision active",

      message:
        suppliedStatus.message ||
        "Ava Vision is active.",

      tone:
        suppliedStatus.tone === "warning"
          ? "warning"
          : suppliedStatus.tone === "good"
            ? "good"
            : "neutral",
    };
  }

  if (!vision) {
    return {
      label: "Starting",
      message:
        "Ava Vision is starting.",
      tone: "neutral",
    };
  }

  const objects =
    Array.isArray(vision?.objects)
      ? vision.objects
      : [];

  const scene =
    readSceneSummary(vision).toLowerCase();

  const unclear =
    objects.length === 0 ||
    scene.includes("no clear") ||
    scene.includes("waiting") ||
    scene.includes("unclear");

  if (unclear) {
    return {
      label: "View unclear",
      message:
        "I’m looking, but the details are not clear enough yet. Try more lighting, hold still, or move a little closer.",
      tone: "warning",
    };
  }

  return {
    label: "Vision ready",
    message:
      "Ava Vision is active and the view is clear.",
    tone: "good",
  };
}

export default function AvaVisionHUD({
  isAdmin = false,
}: AvaVisionHUDProps) {
  const [, refresh] = useState(0);

  useEffect(() => {
    const timer =
      window.setInterval(() => {
        refresh((value) => value + 1);
      }, 500);

    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const frame = getLatestFrame();
  const vision = getLatestVision();

  const objects =
    Array.isArray(vision?.objects)
      ? vision.objects
      : [];

  const scene =
    readSceneSummary(vision);

  const safeStatus =
    buildSafeStatus(vision);

  const statusColor =
    safeStatus.tone === "warning"
      ? "#ffd166"
      : safeStatus.tone === "good"
        ? "#55f5a3"
        : "#8be9ff";

  /*
   * Regular-user experience:
   * No raw frame, labels, percentages, boxes,
   * brightness values, timing, or memory details.
   */
  if (isAdmin === false) {
    return (
      <div
        className="ava-vision-user-hud"
        aria-live="polite"
        aria-label="Ava Vision status"
        style={{
          position: "fixed",
          right: 16,
          bottom: 116,
          width: 286,
          maxWidth:
            "calc(100vw - 24px)",
          boxSizing: "border-box",
          padding: "15px 17px",
          borderRadius: 18,
          border:
            "1px solid rgba(83,225,255,.36)",
          background:
            "linear-gradient(145deg,rgba(5,18,32,.92),rgba(3,10,20,.87))",
          boxShadow:
            "0 0 24px rgba(0,170,255,.18),0 18px 50px rgba(0,0,0,.35)",
          backdropFilter:
            "blur(12px)",
          WebkitBackdropFilter:
            "blur(12px)",
          color: "#effeff",
          zIndex: 99999,
          fontFamily:
            "Inter,system-ui,sans-serif",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent:
              "space-between",
            gap: 12,
          }}
        >
          <span
            style={{
              color: "#a9f6ff",
              fontSize: 11,
              fontWeight: 900,
              letterSpacing: 1.7,
            }}
          >
            AVA VIEW
          </span>

          <span
            style={{
              color: statusColor,
              fontSize: 10,
              fontWeight: 900,
              letterSpacing: 1,
              whiteSpace: "nowrap",
            }}
          >
            ● {safeStatus.label}
          </span>
        </div>

        <div
          className="ava-vision-user-preview"
          style={{
            marginTop: 12,
            overflow: "hidden",
            borderRadius: 14,
            border:
              "1px solid rgba(101,231,255,.32)",
            background: "#02070d",
            boxShadow:
              "inset 0 0 18px rgba(0,180,255,.08)",
          }}
        >
          {frame ? (
            <img
              src={frame}
              alt="What Ava currently sees"
              style={{
                display: "block",
                width: "100%",
                aspectRatio: "4 / 3",
                objectFit: "cover",
                background: "#02070d",
              }}
            />
          ) : (
            <div
              style={{
                width: "100%",
                aspectRatio: "4 / 3",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "rgba(190,245,255,.72)",
                fontSize: 12,
              }}
            >
              Waiting for camera view...
            </div>
          )}
        </div>

        <div
          className="ava-vision-user-message"
          style={{
            marginTop: 10,
            color: "#eaffff",
            fontSize: 13,
            fontWeight: 650,
            lineHeight: 1.45,
          }}
        >
          {safeStatus.message}
        </div>
      </div>
    );
  }

  /*
   * Verified Admin experience:
   * Retains detailed diagnostic information.
   */
  return (
    <div
      className="ava-vision-admin-hud"
      aria-label="Admin vision diagnostics"
      style={{
        position: "fixed",
        right: 18,
        bottom: 116,
        width: 330,
        maxWidth:
          "calc(100vw - 24px)",
        maxHeight:
          "calc(100dvh - 150px)",
        overflowY: "auto",
        borderRadius: 18,
        overflowX: "hidden",
        background:
          "rgba(6,18,35,.92)",
        border:
          "1px solid rgba(56,189,248,.72)",
        boxShadow:
          "0 0 28px rgba(0,170,255,.32)",
        backdropFilter:
          "blur(12px)",
        WebkitBackdropFilter:
          "blur(12px)",
        color: "#fff",
        zIndex: 99999,
        fontFamily:
          "Inter,system-ui,sans-serif",
      }}
    >
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          padding: "12px 15px",
          fontWeight: 800,
          borderBottom:
            "1px solid rgba(255,255,255,.14)",
          background:
            "rgba(5,20,36,.97)",
          display: "flex",
          justifyContent:
            "space-between",
          gap: 10,
        }}
      >
        <span>
          ADMIN VISION DIAGNOSTICS
        </span>

        <span
          style={{
            color: "#55f5a3",
            fontSize: 11,
            whiteSpace: "nowrap",
          }}
        >
          ● LIVE
        </span>
      </div>

      {frame ? (
        <img
          src={frame}
          alt="Admin live vision diagnostic frame"
          style={{
            width: "100%",
            display: "block",
            background: "#000",
          }}
        />
      ) : (
        <div
          style={{
            height: 165,
            display: "flex",
            alignItems: "center",
            justifyContent:
              "center",
            color: "#88dfff",
          }}
        >
          Waiting for camera frame
        </div>
      )}

      <div
        style={{
          padding: 14,
          fontSize: 12,
        }}
      >
        <div
          style={{
            color: "#63ddff",
            fontWeight: 850,
            marginBottom: 5,
          }}
        >
          Scene
        </div>

        <div
          style={{
            marginBottom: 13,
            lineHeight: 1.45,
          }}
        >
          {scene}
        </div>

        <div
          style={{
            color: "#63ddff",
            fontWeight: 850,
            marginBottom: 5,
          }}
        >
          Objects
        </div>

        {objects.length === 0 ? (
          <div
            style={{
              opacity: 0.74,
            }}
          >
            No objects detected
          </div>
        ) : (
          objects
            .slice(0, 12)
            .map(
              (
                object: any,
                index: number
              ) => (
                <div
                  key={`${String(
                    object?.label ||
                      "object"
                  )}-${index}`}
                  style={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    gap: 12,
                    marginBottom: 4,
                  }}
                >
                  <span>
                    {String(
                      object?.label ||
                        "unknown"
                    )}
                  </span>

                  <span
                    style={{
                      color: "#aaf4ff",
                    }}
                  >
                    {Math.round(
                      Number(
                        object?.confidence ||
                          0
                      ) * 100
                    )}
                    %
                  </span>
                </div>
              )
            )
        )}
      </div>
    </div>
  );
}
